import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import { SALES_APPROVAL_PERMISSION } from '../Components/Permission'

const API_URL = process.env.REACT_APP_API_URL

function readPermissionsFromStorage() {
    try {
        const parsed = JSON.parse(localStorage.getItem('permissions') || '[]')
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

function parseApiResponse(raw) {
    let d = raw
    if (typeof d === 'string') {
        try {
            d = JSON.parse(d)
        } catch {
            return raw
        }
    }
    if (d && d.body != null) {
        if (typeof d.body === 'string') {
            try {
                return JSON.parse(d.body)
            } catch {
                return d.body
            }
        }
        return d.body
    }
    return d
}

function parseJsonIfString(value) {
    if (typeof value !== 'string') return value
    try {
        return JSON.parse(value)
    } catch {
        return value
    }
}

function normalizeApiPayload(raw) {
    let obj = raw
    if (!obj || typeof obj !== 'object') return raw
    obj = { ...obj }
    if (typeof obj.data === 'string') {
        obj.data = parseJsonIfString(obj.data)
    }
    if (obj.data && typeof obj.data === 'object' && typeof obj.data.data === 'string') {
        obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) }
    }
    return obj
}

function isApiSuccess(responseData) {
    if (!responseData || typeof responseData !== 'object') return false
    const s = responseData.success
    return s === true || s === 'true' || s === 1 || s === '1'
}

function pickSaleFromResponse(responseData) {
    let dataBlock = responseData?.data != null ? responseData.data : responseData
    if (dataBlock == null) return null
    if (typeof dataBlock === 'string') {
        dataBlock = parseJsonIfString(dataBlock)
    }
    if (!dataBlock || typeof dataBlock !== 'object') return null

    if (dataBlock.sale && typeof dataBlock.sale === 'object') {
        return dataBlock.sale
    }

    if (dataBlock.sales && Array.isArray(dataBlock.sales) && dataBlock.sales.length > 0) {
        return dataBlock.sales[0]
    }

    if (dataBlock._id != null || dataBlock.companyName != null) {
        return dataBlock
    }

    return null
}

function extractCustomersPayload(responseData) {
    const root = responseData
    let dataBlock = root?.data != null ? root.data : root

    if (typeof dataBlock === 'string') {
        dataBlock = parseJsonIfString(dataBlock)
    }
    if (!dataBlock || typeof dataBlock !== 'object') {
        return { customers: [], pagination: {} }
    }

    if (Array.isArray(dataBlock)) {
        return {
            customers: dataBlock,
            pagination: root.pagination && typeof root.pagination === 'object' ? root.pagination : {}
        }
    }

    let list =
        dataBlock.customers ||
        dataBlock.customerList ||
        dataBlock.items ||
        root.customers

    if (list == null && dataBlock.data != null) {
        const inner =
            typeof dataBlock.data === 'string'
                ? parseJsonIfString(dataBlock.data)
                : dataBlock.data
        if (inner && typeof inner === 'object') {
            list = inner.customers || inner.customerList || inner.items
        }
    }

    if (
        (list == null || (Array.isArray(list) && list.length === 0)) &&
        dataBlock.customers == null &&
        dataBlock.customerList == null &&
        dataBlock.items == null &&
        (dataBlock._id != null || dataBlock.customerName != null)
    ) {
        list = [dataBlock]
    }

    const pagination =
        (dataBlock && dataBlock.pagination) || root.pagination || {}

    if (typeof list === 'string') {
        list = parseJsonIfString(list)
    }

    return {
        customers: Array.isArray(list) ? list : [],
        pagination: pagination && typeof pagination === 'object' && !Array.isArray(pagination)
            ? pagination
            : {}
    }
}

function extractContactIdFromSale(sale) {
    const c = sale?.contactId
    if (c == null) return ''
    if (typeof c === 'object') return String(c._id || c.id || '')
    return String(c)
}

const PRIORITIES = ['High', 'Medium', 'Low']

/** Strip invisible / format chars so API values match <option> values. */
const INVISIBLE_UNICODE_RE = /[\u200B-\u200D\uFEFF\u2060\u00A0]/g

function stripForCompare(s) {
    return String(s || '').replace(INVISIBLE_UNICODE_RE, '').trim()
}

function matchValueToOptions(value, options) {
    const v = stripForCompare(value)
    if (!v) return ''
    const direct = options.find((o) => o === value)
    if (direct) return direct
    const byStrip = options.find((o) => stripForCompare(o) === v)
    if (byStrip) return byStrip
    const ci = options.find((o) => stripForCompare(o).toLowerCase() === v.toLowerCase())
    return ci || v
}

const OPPORTUNITY_STAGES = [
    'Contacts',
    'Leads',
    'Opportunity',
    'Prospect',
    'Proposal In Progress',
    'Proposal Submission',
    'Negotiation in Progress',
    'Contract Won',
    'Lost',
    'Hold'
]

const SERVICE_OFFERINGS = [
    'Procure to Pay',
    'e-Sourcing',
    'Procurement Services',
    'Tail Spend Management',
    'SPOT Buys',
    'Inventory Management',
    'Stores Management',
    'Non Moving inventory Liquidation',
    'Vendor Managed Inventory',
    'Master data Management'
]

function normalizeSaleServiceOfferings(sale) {
    const raw = sale?.serviceOffering ?? sale?.service_offering
    const parts = Array.isArray(raw)
        ? raw.map((x) => String(x || '').trim()).filter(Boolean)
        : raw != null && String(raw).trim() !== ''
          ? [String(raw).trim()]
          : []
    const mapped = parts.map((v) => matchValueToOptions(v, SERVICE_OFFERINGS))
    return [...new Set(mapped)]
}

const SalesTrackerEdit = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const syncKeyRef = useRef(null)
    const canShowReviewerRemark = readPermissionsFromStorage().includes(
        SALES_APPROVAL_PERMISSION
    )

    const [customers, setCustomers] = useState([])
    const [loadingCustomers, setLoadingCustomers] = useState(true)
    const [loadingSale, setLoadingSale] = useState(true)
    const [sale, setSale] = useState(null)

    const [selectedCustomerId, setSelectedCustomerId] = useState('')
    const [selectedContactKey, setSelectedContactKey] = useState('')

    const [companyName, setCompanyName] = useState('')
    const [contactId, setContactId] = useState('')
    const [contactPerson, setContactPerson] = useState('')
    const [designation, setDesignation] = useState('')
    const [contactNumber, setContactNumber] = useState('')
    const [email, setEmail] = useState('')
    const [city, setCity] = useState('')
    const [state, setState] = useState('')
    const [priority, setPriority] = useState('')
    const [opportunityStage, setOpportunityStage] = useState('')
    const [selectedServiceOfferings, setSelectedServiceOfferings] = useState([])
    const [serviceOfferingSearchTerm, setServiceOfferingSearchTerm] = useState('')
    const [showServiceOfferingDropdown, setShowServiceOfferingDropdown] = useState(false)
    const [selectedServiceOfferingIndex, setSelectedServiceOfferingIndex] = useState(-1)
    const [filteredServiceOfferings, setFilteredServiceOfferings] = useState(() => [
        ...SERVICE_OFFERINGS
    ])
    /** New entries appended on save (same shape as create-sales). Leave blank to leave history unchanged. */
    const [salesRemarkContent, setSalesRemarkContent] = useState('')
    const [reviewerRemarkContent, setReviewerRemarkContent] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const serviceOfferingDropdownRef = useRef(null)

    useEffect(() => {
        if (!showServiceOfferingDropdown) return undefined
        const handleOutsideClick = (event) => {
            if (
                serviceOfferingDropdownRef.current &&
                !serviceOfferingDropdownRef.current.contains(event.target)
            ) {
                setShowServiceOfferingDropdown(false)
                setSelectedServiceOfferingIndex(-1)
            }
        }
        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [showServiceOfferingDropdown])

    useEffect(() => {
        syncKeyRef.current = null
    }, [id])

    useEffect(() => {
        const fetchCustomers = async () => {
            setLoadingCustomers(true)
            try {
                const payload = {
                    action: 'get-all-customers',
                    page: 1,
                    limit: 500,
                    search: ''
                }
                const response = await axios.post(API_URL, payload)
                const responseData = normalizeApiPayload(parseApiResponse(response.data))
                const { customers: list } = extractCustomersPayload(responseData)
                const ok =
                    isApiSuccess(responseData) ||
                    (Array.isArray(list) && list.length > 0)
                if (ok) {
                    setCustomers(list)
                }
            } catch (error) {
                console.error('Fetch customers for sales edit:', error)
            } finally {
                setLoadingCustomers(false)
            }
        }
        fetchCustomers()
    }, [])

    useEffect(() => {
        const fetchSale = async () => {
            if (!id) {
                toast.error('Sales record ID is missing', { autoClose: 2000 })
                navigate('/salesTracker')
                return
            }

            setLoadingSale(true)
            setSale(null)
            try {
                const payload = {
                    action: 'get-sales-by-id',
                    id
                }
                const response = await axios.post(API_URL, payload)
                const responseData = normalizeApiPayload(parseApiResponse(response.data))

                if (isApiSuccess(responseData)) {
                    const row = pickSaleFromResponse(responseData)
                    if (row) {
                        setSale(row)
                    } else {
                        toast.error('Sales record not found', { autoClose: 2000 })
                        navigate('/salesTracker')
                    }
                } else {
                    toast.error(responseData?.message || 'Failed to load sales record', { autoClose: 2000 })
                    navigate('/salesTracker')
                }
            } catch (error) {
                console.error('Fetch sales for edit:', error)
                const errData = error.response?.data ? parseApiResponse(error.response.data) : null
                const msg =
                    typeof errData === 'object' && errData?.message
                        ? errData.message
                        : 'An error occurred while loading the sales record'
                toast.error(msg, { autoClose: 2000 })
                navigate('/salesTracker')
            } finally {
                setLoadingSale(false)
            }
        }
        fetchSale()
    }, [id, navigate])

    useEffect(() => {
        if (!sale) return
        setCompanyName(sale.companyName || '')
        setContactPerson(sale.contactPerson || '')
        setDesignation(sale.designation || '')
        setContactNumber(String(sale.contactNumber || '').replace(/\D/g, '').slice(0, 10))
        setEmail(sale.email || '')
        setCity(sale.city || '')
        setState(sale.state || '')
        setPriority(sale.priority || '')
        setOpportunityStage(
            matchValueToOptions(
                sale.opportunityStage ?? sale.opportunity_stage,
                OPPORTUNITY_STAGES
            )
        )
        setSelectedServiceOfferings(normalizeSaleServiceOfferings(sale))
        setContactId(extractContactIdFromSale(sale))
    }, [sale])

    useEffect(() => {
        if (!sale || !customers.length) return
        const sid = sale._id || sale.id
        if (syncKeyRef.current === sid) return

        const cid = extractContactIdFromSale(sale)
        if (!cid) {
            syncKeyRef.current = sid
            return
        }

        const cust = customers.find((c) => String(c._id || c.id) === String(cid))
        if (cust) {
            setSelectedCustomerId(String(cust._id || cust.id))
            const contacts = Array.isArray(cust.contacts) ? cust.contacts : []
            const match = contacts.find(
                (ct) =>
                    (sale.email && ct.email === sale.email) ||
                    (sale.contactPerson && ct.contactPerson === sale.contactPerson)
            )
            if (match) {
                const idx = contacts.indexOf(match)
                const k = match._id != null ? String(match._id) : `idx-${idx}`
                setSelectedContactKey(k)
            } else {
                setSelectedContactKey('')
            }
        } else {
            setSelectedCustomerId('')
            setSelectedContactKey('')
        }
        syncKeyRef.current = sid
    }, [sale, customers])

    const selectedCustomer = customers.find(
        (c) => String(c._id || c.id) === String(selectedCustomerId)
    )

    const contactOptions = useMemo(() => {
        if (!selectedCustomer || !Array.isArray(selectedCustomer.contacts)) return []
        return selectedCustomer.contacts.map((ct, idx) => ({
            key: ct._id != null ? String(ct._id) : `idx-${idx}`,
            contact: ct,
            label: `${ct.contactPerson || 'Contact'}${ct.email ? ` — ${ct.email}` : ''}`
        }))
    }, [selectedCustomer])

    const contactDetailsLocked =
        contactOptions.length > 0 && selectedContactKey !== ''

    const lockedInputClass = contactDetailsLocked
        ? 'form-control bg-light text-muted'
        : 'form-control'

    const applyContactFields = (ct) => {
        if (!ct) return
        setContactPerson(ct.contactPerson || '')
        setDesignation(ct.designation || '')
        setContactNumber((ct.contactNumber || '').replace(/\D/g, '').slice(0, 10))
        setEmail(ct.email || '')
        setCity(ct.city || '')
        setState(ct.state || '')
    }

    const clearContactFields = () => {
        setContactPerson('')
        setDesignation('')
        setContactNumber('')
        setEmail('')
        setCity('')
        setState('')
    }

    const handleCustomerChange = (e) => {
        const custId = e.target.value
        setSelectedCustomerId(custId)
        setSelectedContactKey('')
        const cust = customers.find((c) => String(c._id || c.id) === String(custId))
        if (cust) {
            setCompanyName(cust.customerName || '')
            setContactId(String(cust._id || cust.id || ''))
            clearContactFields()
        } else {
            setCompanyName('')
            setContactId('')
            clearContactFields()
        }
    }

    const handleContactSelectChange = (e) => {
        const key = e.target.value
        setSelectedContactKey(key)
        if (!key) {
            clearContactFields()
            return
        }
        const opt = contactOptions.find((o) => o.key === key)
        if (opt?.contact) applyContactFields(opt.contact)
    }

    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 10)
        setContactNumber(value)
    }

    const handleCityChange = (e) => {
        setCity(e.target.value.replace(new RegExp('[^a-zA-Z\\s,/-]', 'g'), ''))
    }

    const handleStateChange = (e) => {
        setState(e.target.value.replace(/[^a-zA-Z\s]/g, ''))
    }

    const handleServiceOfferingSearchChange = (e) => {
        const searchValue = e.target.value
        setServiceOfferingSearchTerm(searchValue)
        setShowServiceOfferingDropdown(true)
        setSelectedServiceOfferingIndex(-1)

        if (searchValue.trim() === '') {
            setFilteredServiceOfferings([...SERVICE_OFFERINGS])
        } else {
            const q = searchValue.toLowerCase()
            setFilteredServiceOfferings(
                SERVICE_OFFERINGS.filter((s) => s.toLowerCase().includes(q))
            )
        }
    }

    const handleServiceOfferingToggle = (name) => {
        const n = String(name || '').trim()
        if (!n) return

        setSelectedServiceOfferings((prev) => {
            if (prev.includes(n)) return prev.filter((x) => x !== n)
            return [...prev, n]
        })
    }

    const handleServiceOfferingSelectAllVisible = () => {
        setSelectedServiceOfferings((prev) => {
            const next = new Set(prev)
            filteredServiceOfferings.forEach((item) => next.add(item))
            return Array.from(next)
        })
    }

    const handleServiceOfferingClearAll = () => {
        setSelectedServiceOfferings([])
    }

    const handleServiceOfferingKeyDown = (e) => {
        if (!showServiceOfferingDropdown || filteredServiceOfferings.length === 0) return

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setSelectedServiceOfferingIndex((prev) =>
                    prev < filteredServiceOfferings.length - 1 ? prev + 1 : prev
                )
                break
            case 'ArrowUp':
                e.preventDefault()
                setSelectedServiceOfferingIndex((prev) => (prev > 0 ? prev - 1 : -1))
                break
            case 'Enter':
            case ' ':
                e.preventDefault()
                if (
                    selectedServiceOfferingIndex >= 0 &&
                    selectedServiceOfferingIndex < filteredServiceOfferings.length
                ) {
                    handleServiceOfferingToggle(
                        filteredServiceOfferings[selectedServiceOfferingIndex]
                    )
                }
                break
            case 'Escape':
                setShowServiceOfferingDropdown(false)
                setSelectedServiceOfferingIndex(-1)
                break
            default:
                break
        }
    }

    const handleServiceOfferingFocus = () => {
        setShowServiceOfferingDropdown(true)
        setSelectedServiceOfferingIndex(-1)
        const term = serviceOfferingSearchTerm.trim()
        if (term === '') {
            setFilteredServiceOfferings([...SERVICE_OFFERINGS])
        } else {
            const q = term.toLowerCase()
            setFilteredServiceOfferings(
                SERVICE_OFFERINGS.filter((s) => s.toLowerCase().includes(q))
            )
        }
    }

    const validateForm = () => {
        if (!selectedCustomerId) {
            toast.error('Please select a customer', { autoClose: 2000 })
            return false
        }
        if (contactOptions.length > 0 && !selectedContactKey) {
            toast.error('Please select a contact', { autoClose: 2000 })
            return false
        }
        if (!priority) {
            toast.error('Please select priority', { autoClose: 2000 })
            return false
        }
        if (!opportunityStage) {
            toast.error('Please select opportunity stage', { autoClose: 2000 })
            return false
        }
        if (!companyName.trim()) {
            toast.error('Company name is required', { autoClose: 2000 })
            return false
        }
        if (!contactId.trim()) {
            toast.error('Customer reference is missing', { autoClose: 2000 })
            return false
        }
        if (!contactPerson.trim()) {
            toast.error('Contact person is required', { autoClose: 2000 })
            return false
        }
        if (!email.trim()) {
            toast.error('Email is required', { autoClose: 2000 })
            return false
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            toast.error('Please enter a valid email', { autoClose: 2000 })
            return false
        }
        if (!contactNumber.trim() || contactNumber.length !== 10) {
            toast.error('Contact number must be 10 digits', { autoClose: 2000 })
            return false
        }
        if (!city.trim()) {
            toast.error('City is required', { autoClose: 2000 })
            return false
        }
        if (!state.trim()) {
            toast.error('State is required', { autoClose: 2000 })
            return false
        }
        if (selectedServiceOfferings.length === 0) {
            toast.error('Please select at least one service offering', { autoClose: 2000 })
            return false
        }
        return true
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validateForm()) return

        const requestUserId = localStorage.getItem('id')
        if (!requestUserId) {
            toast.error('User session expired. Please login again.', { autoClose: 2000 })
            setTimeout(() => navigate('/'), 2000)
            return
        }

        const salesRecordId = id || sale?._id || sale?.id
        if (!salesRecordId) {
            toast.error('Sales record ID is missing', { autoClose: 2000 })
            return
        }

        setIsLoading(true)
        try {
            const userName = localStorage.getItem('name') || ''

            const payload = {
                action: 'update-sales',
                id: String(salesRecordId),
                requestUserId,
                companyName: companyName.trim(),
                contactPerson: contactPerson.trim(),
                designation: designation.trim(),
                contactNumber: contactNumber.trim(),
                email: email.trim(),
                city: city.trim(),
                state: state.trim(),
                priority,
                opportunityStage,
                serviceOffering: selectedServiceOfferings,
                contactId: contactId.trim()
            }

            const salesText = salesRemarkContent.trim()
            if (salesText) {
                payload.salesRemark = {
                    content: salesText,
                    userId: requestUserId,
                    userName: userName.trim()
                }
            }

            const reviewerText = reviewerRemarkContent.trim()
            if (canShowReviewerRemark && reviewerText) {
                payload.reviewerRemark = {
                    content: reviewerText,
                    userId: requestUserId,
                    userName: userName.trim()
                }
            }

            const response = await axios.post(API_URL, payload)
            const responseData = normalizeApiPayload(parseApiResponse(response.data))

            if (isApiSuccess(responseData)) {
                toast.success(responseData.message || 'Sales record updated successfully', {
                    autoClose: 2000
                })
                navigate('/salesTracker', { replace: true })
            } else {
                toast.error(responseData?.message || 'Failed to update sales record', { autoClose: 2000 })
            }
        } catch (error) {
            console.error('Update sales error:', error)
            const errData = error.response?.data
                ? parseApiResponse(error.response.data)
                : null
            const msg =
                typeof errData === 'object' && errData?.message
                    ? errData.message
                    : error?.message || 'An error occurred while updating the sales record'
            toast.error(msg, { autoClose: 2000 })
        } finally {
            setIsLoading(false)
        }
    }

    const pageBusy = loadingSale || loadingCustomers

    return (
        <div>
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">Edit Sales</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/salesTracker">
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    <form className="form-horizontal" onSubmit={handleSubmit}>
                                        <div className="card-body">
                                            {pageBusy ? (
                                                <div className="text-center py-4">
                                                    <PulseLoader color="#1199B2" size={10} />
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">
                                                            Customer <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="col-12 col-md-8">
                                                            <select
                                                                className="form-control"
                                                                value={selectedCustomerId}
                                                                onChange={handleCustomerChange}
                                                                required
                                                            >
                                                                <option value=""> Select customer</option>
                                                                {customers.map((c) => (
                                                                    <option key={c._id || c.id} value={String(c._id || c.id)}>
                                                                        {c.customerName || '—'}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <small className="text-muted">
                                                                Linked customer for this sale. Change to update the customer reference.
                                                            </small>
                                                        </div>
                                                    </div>

                                                    {contactOptions.length > 0 && (
                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-4 col-form-label">
                                                                Contact <span className="text-danger">*</span>
                                                            </label>
                                                            <div className="col-12 col-md-8">
                                                                <select
                                                                    className="form-control"
                                                                    value={selectedContactKey}
                                                                    onChange={handleContactSelectChange}
                                                                >
                                                                    <option value="">— Select contact —</option>
                                                                    {contactOptions.map((o) => (
                                                                        <option key={o.key} value={o.key}>
                                                                            {o.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {contactDetailsLocked && (
                                                                    <small className="text-muted d-block mt-1">
                                                                        Contact details below are taken from your selection and cannot be
                                                                        edited.
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Company Name</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Company name"
                                                                value={companyName}
                                                                onChange={(e) => setCompanyName(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Contact Person</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="text"
                                                                className={lockedInputClass}
                                                                placeholder="Contact person"
                                                                value={contactPerson}
                                                                onChange={(e) => setContactPerson(e.target.value)}
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Designation</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="text"
                                                                className={lockedInputClass}
                                                                placeholder="Designation"
                                                                value={designation}
                                                                onChange={(e) => setDesignation(e.target.value)}
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Email</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="email"
                                                                className={lockedInputClass}
                                                                placeholder="Email"
                                                                value={email}
                                                                onChange={(e) => setEmail(e.target.value)}
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Phone</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="tel"
                                                                className={lockedInputClass}
                                                                placeholder="10-digit mobile"
                                                                value={contactNumber}
                                                                onChange={handlePhoneChange}
                                                                onKeyPress={(e) => {
                                                                    if (
                                                                        !/[0-9]/.test(e.key) &&
                                                                        e.key !== 'Backspace' &&
                                                                        e.key !== 'Delete' &&
                                                                        e.key !== 'Tab'
                                                                    ) {
                                                                        e.preventDefault()
                                                                    }
                                                                }}
                                                                maxLength={10}
                                                                inputMode="numeric"
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">City</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="text"
                                                                className={lockedInputClass}
                                                                placeholder="City"
                                                                value={city}
                                                                onChange={handleCityChange}
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">State</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="text"
                                                                className={lockedInputClass}
                                                                placeholder="State"
                                                                value={state}
                                                                onChange={handleStateChange}
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">
                                                            Priority <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="col-12 col-md-8">
                                                            <select
                                                                className="form-control"
                                                                value={priority}
                                                                onChange={(e) => setPriority(e.target.value)}
                                                            >
                                                                <option value="">Select priority</option>
                                                                {PRIORITIES.map((p) => (
                                                                    <option key={p} value={p}>
                                                                        {p}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">
                                                            Opportunity Stage <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="col-12 col-md-8">
                                                            <select
                                                                className="form-control"
                                                                value={opportunityStage}
                                                                onChange={(e) => setOpportunityStage(e.target.value)}
                                                            >
                                                                <option value="">Select opportunity stage</option>
                                                                {opportunityStage &&
                                                                    !OPPORTUNITY_STAGES.includes(opportunityStage) && (
                                                                        <option value={opportunityStage}>
                                                                            {opportunityStage}
                                                                        </option>
                                                                    )}
                                                                {OPPORTUNITY_STAGES.map((s) => (
                                                                    <option key={s} value={s}>
                                                                        {s}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">
                                                            Service Offering{' '}
                                                            <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="col-12 col-md-8">
                                                            {selectedServiceOfferings.length > 0 && (
                                                                <div className="mb-2">
                                                                    {selectedServiceOfferings.map((label) => (
                                                                        <span
                                                                            key={label}
                                                                            className="badge badge-info mr-2 mb-1"
                                                                            style={{
                                                                                fontSize: '12px',
                                                                                fontWeight: 550
                                                                            }}
                                                                        >
                                                                            {label}
                                                                            <span
                                                                                className="ml-2 crsr_pntr"
                                                                                onMouseDown={(ev) => {
                                                                                    ev.preventDefault()
                                                                                    handleServiceOfferingToggle(label)
                                                                                }}
                                                                                title="Remove"
                                                                            >
                                                                                <i className="fa-solid fa-xmark" />
                                                                            </span>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <div
                                                                ref={serviceOfferingDropdownRef}
                                                                style={{ position: 'relative' }}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="form-control text-left d-flex align-items-center justify-content-between"
                                                                    onClick={() => {
                                                                        if (showServiceOfferingDropdown) {
                                                                            setShowServiceOfferingDropdown(false)
                                                                            setSelectedServiceOfferingIndex(-1)
                                                                        } else {
                                                                            handleServiceOfferingFocus()
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        height: 'auto',
                                                                        minHeight: '38px',
                                                                        whiteSpace: 'normal'
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            color:
                                                                                selectedServiceOfferings.length > 0
                                                                                    ? '#212529'
                                                                                    : '#6c757d'
                                                                        }}
                                                                    >
                                                                        {selectedServiceOfferings.length > 0
                                                                            ? `${selectedServiceOfferings.length} selected`
                                                                            : 'Select service offerings'}
                                                                    </span>
                                                                    <i
                                                                        className={`fa-solid ${
                                                                            showServiceOfferingDropdown
                                                                                ? 'fa-chevron-up'
                                                                                : 'fa-chevron-down'
                                                                        }`}
                                                                        style={{ fontSize: '12px', color: '#6c757d' }}
                                                                    />
                                                                </button>

                                                                {showServiceOfferingDropdown && (
                                                                    <div
                                                                        style={{
                                                                            position: 'absolute',
                                                                            bottom: '100%',
                                                                            left: 0,
                                                                            right: 0,
                                                                            marginBottom: '4px',
                                                                            backgroundColor: '#fff',
                                                                            border: '1px solid #ced4da',
                                                                            borderRadius: '4px',
                                                                            zIndex: 1050,
                                                                            boxShadow: '0 -4px 12px rgba(0,0,0,0.12)'
                                                                        }}
                                                                        onMouseDown={(ev) => ev.preventDefault()}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                padding: '8px',
                                                                                borderBottom: '1px solid #e9ecef'
                                                                            }}
                                                                        >
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                placeholder="Search service offerings..."
                                                                                value={serviceOfferingSearchTerm}
                                                                                onChange={handleServiceOfferingSearchChange}
                                                                                onKeyDown={handleServiceOfferingKeyDown}
                                                                                onFocus={handleServiceOfferingFocus}
                                                                                autoComplete="off"
                                                                                autoFocus
                                                                            />
                                                                            <div className="d-flex justify-content-between align-items-center mt-2">
                                                                                <small className="text-muted">
                                                                                    {selectedServiceOfferings.length} selected
                                                                                </small>
                                                                                <div>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-link btn-sm p-0 mr-3"
                                                                                        onClick={handleServiceOfferingSelectAllVisible}
                                                                                    >
                                                                                        Select all
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-link btn-sm p-0 text-danger"
                                                                                        onClick={handleServiceOfferingClearAll}
                                                                                        disabled={
                                                                                            selectedServiceOfferings.length === 0
                                                                                        }
                                                                                    >
                                                                                        Clear
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div
                                                                            style={{
                                                                                maxHeight: '220px',
                                                                                overflowY: 'auto'
                                                                            }}
                                                                        >
                                                                            {filteredServiceOfferings.length > 0 ? (
                                                                                filteredServiceOfferings.map(
                                                                                    (offering, index) => {
                                                                                        const checked =
                                                                                            selectedServiceOfferings.includes(
                                                                                                offering
                                                                                            )
                                                                                        const isHighlighted =
                                                                                            index ===
                                                                                            selectedServiceOfferingIndex
                                                                                        const rowBg = isHighlighted
                                                                                            ? '#e9ecef'
                                                                                            : checked
                                                                                              ? '#f0f9fb'
                                                                                              : '#fff'
                                                                                        return (
                                                                                            <div
                                                                                                key={offering}
                                                                                                className="d-flex align-items-center"
                                                                                                style={{
                                                                                                    padding: '8px 12px',
                                                                                                    cursor: 'pointer',
                                                                                                    borderBottom:
                                                                                                        '1px solid #f0f0f0',
                                                                                                    backgroundColor: rowBg,
                                                                                                    fontWeight: 'normal'
                                                                                                }}
                                                                                                onClick={() =>
                                                                                                    handleServiceOfferingToggle(
                                                                                                        offering
                                                                                                    )
                                                                                                }
                                                                                                onMouseEnter={() =>
                                                                                                    setSelectedServiceOfferingIndex(
                                                                                                        index
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    className="mr-2"
                                                                                                    checked={checked}
                                                                                                    onChange={() =>
                                                                                                        handleServiceOfferingToggle(
                                                                                                            offering
                                                                                                        )
                                                                                                    }
                                                                                                    onClick={(e) =>
                                                                                                        e.stopPropagation()
                                                                                                    }
                                                                                                />
                                                                                                <span
                                                                                                    style={{
                                                                                                        fontSize: '14px',
                                                                                                        fontWeight: 400
                                                                                                    }}
                                                                                                >
                                                                                                    {offering}
                                                                                                </span>
                                                                                            </div>
                                                                                        )
                                                                                    }
                                                                                )
                                                                            ) : (
                                                                                <div
                                                                                    style={{
                                                                                        padding: '10px 12px',
                                                                                        color: '#6c757d',
                                                                                        fontSize: '13px'
                                                                                    }}
                                                                                >
                                                                                    No service offerings found
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        <div
                                                                            className="text-right"
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                borderTop: '1px solid #e9ecef',
                                                                                backgroundColor: '#f8f9fa'
                                                                            }}
                                                                        >
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-blue"
                                                                                onClick={() => {
                                                                                    setShowServiceOfferingDropdown(false)
                                                                                    setSelectedServiceOfferingIndex(-1)
                                                                                }}
                                                                            >
                                                                                Done
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">
                                                            Sales remark{' '}
                                                            <small className="text-muted font-weight-normal">
                                                                (optional — appends to history)
                                                            </small>
                                                        </label>
                                                        <div className="col-12 col-md-8">
                                                            <textarea
                                                                className="form-control"
                                                                rows={3}
                                                                placeholder="Add a new sales note when updating"
                                                                value={salesRemarkContent}
                                                                onChange={(e) =>
                                                                    setSalesRemarkContent(e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    {canShowReviewerRemark && (
                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-4 col-form-label">
                                                                Reviewer remark{' '}
                                                                <small className="text-muted font-weight-normal">
                                                                    (optional)
                                                                </small>
                                                            </label>
                                                            <div className="col-12 col-md-8">
                                                                <textarea
                                                                    className="form-control"
                                                                    rows={3}
                                                                    placeholder="Add a reviewer note when updating"
                                                                    value={reviewerRemarkContent}
                                                                    onChange={(e) =>
                                                                        setReviewerRemarkContent(e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        <div className="card-footer text-center">
                                            <button
                                                type="submit"
                                                className="btn btn-blue"
                                                disabled={isLoading || pageBusy}
                                            >
                                                {isLoading ? 'Saving...' : 'Update'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SalesTrackerEdit
